import { Box, Drawer, IconButton, Typography, useMediaQuery, type Theme } from '@mui/material';
import { Baby, PanelLeft } from 'lucide-react';
import { Component, Suspense, useEffect, useMemo, useState, type ErrorInfo, type ReactElement, type ReactNode } from 'react';
import { Error as RaError, Loading, usePermissions } from 'react-admin';
import { useLocation } from 'react-router';

import type { AdminRole } from '@/admin/adminApi';
import { RESOURCE_CATALOG } from '@/resources/catalog';

import { AdminMenu } from './AdminMenu';
import { Icon } from './kit';
import { activeEntryFor, visibleSections, type ActiveNavEntry } from './navigation';
import { CommandPaletteProvider } from './palette/CommandPaletteProvider';
import { SearchTrigger } from './palette/SearchTrigger';
import { Breadcrumb, BreadcrumbLeafProvider } from './shell/Breadcrumb';
import { NavBadgesProvider } from './shell/navBadges';
import { ThemeToggle, UserMenu } from './shell/UserMenu';

/**
 * Coque de l'application (maquette B1, ecran P02 ; variante etroite P20).
 *
 * - **Barre laterale de 260 px** : marque, bandeau d'environnement, rubriques du
 *   registre (`navigation.ts`). **Sous 900 px**, elle devient un tiroir ouvert
 *   par le bouton « panneau » de la barre compacte.
 * - **Barre superieure de 64 px** : fil d'Ariane derive du registre, champ qui
 *   ouvre la palette de commandes (`palette/`, BO-21), bascule de theme, menu de
 *   l'operateur avec son role reel. Ni cloche (aucun ecran d'alertes), ni aide
 *   (aucun contenu servi) : un bouton qui ne mene a rien est une promesse fausse.
 * - **Reperes** : lien d'evitement, `nav`, `header`, `main` ; titre de l'onglet
 *   tenu a jour depuis l'entree courante (WCAG 2.4.2).
 *
 * Elle remplace le `Layout` de React Admin plutot que de l'habiller : celui-ci
 * place la barre laterale **dans** `main` et sous une barre pleine largeur, la
 * ou la maquette met la barre laterale sur toute la hauteur, a cote de la barre
 * superieure. Ce que le `Layout` d'origine apportait est repris : la frontiere
 * d'erreur (ecran d'erreur de React Admin, qui se reinitialise de lui-meme au
 * changement de page) et l'attente des pages differees.
 */

const MAIN_ID = 'main-content';
const DRAWER_ID = 'navigation-laterale';
const SIDEBAR_WIDTH = 260;

/** Frontiere d'erreur : l'ecran de React Admin, sans `react-error-boundary`. */
class PageErrorBoundary extends Component<{ readonly children: ReactNode }, { error: Error | null; info: ErrorInfo | undefined }> {
  override state: { error: Error | null; info: ErrorInfo | undefined } = { error: null, info: undefined };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(_error: Error, info: ErrorInfo): void {
    this.setState({ info });
  }

  override render(): ReactNode {
    const { error, info } = this.state;

    return error === null ? (
      this.props.children
    ) : (
      <RaError
        error={error}
        errorInfo={info}
        resetErrorBoundary={() => {
          this.setState({ error: null, info: undefined });
        }}
      />
    );
  }
}

/**
 * Lien d'evitement. Le routeur de React Admin vit dans le fragment de l'URL : un
 * `href="#main-content"` changerait de page. Le lien deplace donc le focus lui-meme.
 */
const SkipLink = (): ReactElement => (
  <Box
    component="a"
    href={`#${MAIN_ID}`}
    onClick={(event) => {
      event.preventDefault();
      document.getElementById(MAIN_ID)?.focus();
    }}
    sx={{
      position: 'absolute',
      left: 16,
      top: -64,
      zIndex: (theme) => theme.zIndex.tooltip + 1,
      px: 2,
      py: 1,
      borderRadius: '10px',
      bgcolor: 'tokens.surface',
      color: 'tokens.textStrong',
      fontWeight: 700,
      border: 2,
      borderColor: 'tokens.primary',
      '&:focus': { top: 12 },
    }}
  >
    Aller au contenu
  </Box>
);

/**
 * Feuille deduite pour une collection du catalogue rangee sous l'explorateur
 * (« Données / Explorateur de données / Lots de lait »). Transitoire : disparait
 * avec le catalogue.
 */
const catalogLeaf = (pathname: string, active: ActiveNavEntry | null): string | null => {
  if (active === null || active.exact || active.entry.target.kind === 'resource') {
    return null;
  }

  return (
    RESOURCE_CATALOG.find(
      (descriptor) => pathname === `/${descriptor.name}` || pathname.startsWith(`/${descriptor.name}/`),
    )?.label ?? null
  );
};

/**
 * Cible du `<Title>` de React Admin, rendue en `h1` en tete du contenu.
 *
 * Les ecrans generes du catalogue (`List`, `Show`, `Create`, `Edit`) ne nomment
 * leur page que par la propriete `title`, que React Admin rend dans l'element
 * `#react-admin-title` — et seulement s'il existe. Sans cette cible, « Documents
 * légaux — modification » disparaissait, et sur un telephone rien ne disait plus
 * ce qu'on editait. Vide (un ecran qui porte son propre `h1` n'y ecrit rien),
 * elle est masquee, donc absente de l'arbre d'accessibilite.
 */
const PageTitleSlot = (): ReactElement => (
  <Typography
    component="h1"
    id="react-admin-title"
    sx={{ m: 0, mb: 2, fontSize: { xs: 24, sm: 28 }, fontWeight: 800, lineHeight: { xs: '30px', sm: '34px' }, '&:empty': { display: 'none' } }}
  />
);

const TopBar = ({
  narrow,
  drawerOpen,
  onOpenDrawer,
  active,
  leaf,
  pageTitle,
}: {
  readonly narrow: boolean;
  readonly drawerOpen: boolean;
  readonly onOpenDrawer: () => void;
  readonly active: ActiveNavEntry | null;
  readonly leaf: string | null;
  /** Nom de la page courante, affiche par la barre compacte a la place du fil d'Ariane. */
  readonly pageTitle: string | null;
}): ReactElement => (
  <Box
    component="header"
    sx={{
      position: 'sticky',
      top: 0,
      zIndex: (theme) => theme.zIndex.appBar,
      height: 64,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: { xs: 2, md: 4 },
      bgcolor: 'tokens.surface',
      borderBottom: 1,
      borderColor: 'tokens.border',
    }}
  >
    {narrow ? (
      <>
        <IconButton
          aria-label="Ouvrir la navigation"
          // Le tiroir ferme n'est pas dans le DOM : on ne designe que ce qui existe.
          aria-controls={drawerOpen ? DRAWER_ID : undefined}
          aria-expanded={drawerOpen}
          onClick={onOpenDrawer}
          sx={{ width: 40, height: 40, border: 1, borderColor: 'tokens.border', color: 'tokens.textMuted' }}
        >
          <Icon icon={PanelLeft} size={18} />
        </IconButton>
        <Box
          aria-hidden
          sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: 'tokens.primary', color: 'tokens.textOnPrimary', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
        >
          <Icon icon={Baby} size={16} />
        </Box>
        {/* Sous 900 px, le fil d'Ariane disparait : la barre dit au moins ou
            l'on est. Ce n'est pas le titre de la page (le `h1` est dans `main`). */}
        <Typography noWrap sx={{ fontSize: 16, fontWeight: 800, minWidth: 0 }}>
          {pageTitle ?? 'Administration'}
        </Typography>
      </>
    ) : (
      <Breadcrumb active={active} leaf={leaf} />
    )}
    <Box sx={{ flex: 1 }} />
    <SearchTrigger compact={narrow} />
    <ThemeToggle />
    <UserMenu compact={narrow} />
  </Box>
);

export const BackofficeLayout = ({ children }: { readonly children: ReactNode }): ReactElement => {
  const narrow = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pageLeaf, setPageLeaf] = useState<string | null>(null);
  const { pathname } = useLocation();
  const { permissions } = usePermissions<AdminRole | null>();

  // Memoisees sur le role : les pastilles ne relisent que les entrees qui
  // apparaissent quand le role arrive, jamais a chaque rendu.
  const sections = useMemo(() => visibleSections(permissions ?? null), [permissions]);
  const renderedEntries = useMemo(() => sections.flatMap((section) => section.entries), [sections]);
  const active = activeEntryFor(pathname, sections);
  const leaf = pageLeaf ?? catalogLeaf(pathname, active);
  const pageTitle = leaf ?? active?.entry.label ?? null;

  useEffect(() => {
    document.title = pageTitle === null ? 'MiaMiam · Administration' : `${pageTitle} · MiaMiam Administration`;
  }, [pageTitle]);

  const closeDrawer = (): void => {
    setDrawerOpen(false);
  };

  const menu = <AdminMenu sections={sections} active={active} onNavigate={narrow ? closeDrawer : undefined} />;

  return (
    <NavBadgesProvider entries={renderedEntries}>
      <CommandPaletteProvider sections={sections}>
        <BreadcrumbLeafProvider value={setPageLeaf}>
          <SkipLink />
          <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'tokens.background' }}>
            {narrow ? (
              <Drawer
                id={DRAWER_ID}
                open={drawerOpen}
                onClose={closeDrawer}
                slotProps={{ paper: { sx: { width: SIDEBAR_WIDTH, maxWidth: '85vw', bgcolor: 'tokens.surface' } } }}
              >
                {menu}
              </Drawer>
            ) : (
              // La colonne porte le fond sur toute la hauteur de la page ; le menu,
              // lui, reste colle en haut de l'ecran et defile seul s'il deborde.
              <Box sx={{ width: SIDEBAR_WIDTH, flexShrink: 0, bgcolor: 'tokens.surface', borderRight: 1, borderColor: 'tokens.border' }}>
                <Box sx={{ position: 'sticky', top: 0, maxHeight: '100vh', overflowY: 'auto' }}>{menu}</Box>
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <TopBar
                narrow={narrow}
                drawerOpen={drawerOpen}
                onOpenDrawer={() => {
                  setDrawerOpen(true);
                }}
                active={active}
                leaf={leaf}
                pageTitle={pageTitle}
              />
              <Box
                component="main"
                id={MAIN_ID}
                tabIndex={-1}
                sx={{ flex: 1, minWidth: 0, px: { xs: 2, md: 4 }, pt: { xs: 2, md: 3.5 }, pb: 5, outline: 'none' }}
              >
                {/* Pas de `key` par chemin : elle demonterait la liste d'un volet
                    liste-detail a chaque ouverture de fiche. L'ecran d'erreur de
                    React Admin se reinitialise seul au changement de page. */}
                <PageTitleSlot />
                <PageErrorBoundary>
                  <Suspense fallback={<Loading />}>{children}</Suspense>
                </PageErrorBoundary>
              </Box>
            </Box>
          </Box>
        </BreadcrumbLeafProvider>
      </CommandPaletteProvider>
    </NavBadgesProvider>
  );
};
